import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { Form, Formik } from "formik";
import { AnimatePresence, motion } from "framer-motion";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { RowSelectionState } from "react-table-8.10.7";
import { useCategorizeGroupTransactionsMutation } from "store/apis/transactions";
import { setSelectedTransactionIds } from "store/slices/transactions";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { flattenTypes } from "utils/flattenCOA";
import { object, string } from "yup";
import { TransactionsView } from "components/TransactionsView/TransactionsView";
import { Transactions } from "types/Models/books";
import {
  coaOptions,
  getCOALabel,
} from "components/Transaction/TransactionColumn";
import { DisclaimerUI } from "components/Transaction/BulkCategorise";
import { AddNewAccount } from "components/ChartOfAccounts/AddNewAccount";

type BulkRecategoriseProps = ModalProps & {
  deselectRows: () => void;
  transactions: RowSelectionState;
};

export const BulkRecategorise = ({
  close,
  isOpen,
  deselectRows,
  transactions = {},
}: BulkRecategoriseProps) => {
  const dispatch = useDispatch();

  const { chartOfAccounts = [] } = useChartOfAccounts({
    hiddenCategoryTypes: ["BANK_ACCOUNT"],
  });

  const flattenChartOfAccounts = coaOptions(chartOfAccounts);
  const { successToast, alertToast } = useToast();
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const [groupCategorize] = useCategorizeGroupTransactionsMutation();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    Object.fromEntries(
      Object.entries(transactions).map(([key, value]) => {
        const parsedKey = JSON.parse(key);
        return [parsedKey.uuid, value];
      })
    )
  );

  const selectedTxnIds = Object.entries(rowSelection);

  const selectedTransactions = useMemo(() => {
    return Object.entries(transactions)
      .filter(([_, selected]) => selected)
      .map(([key]) => {
        return JSON.parse(key);
      });
  }, [transactions]);

  const parentSelectorModal = useModal();

  const onClose = () => {
    close();
    dispatch(setSelectedTransactionIds([]));
  };

  const onSubmit = async ({ category }: { category: string }) => {
    try {
      await groupCategorize({
        groupId,
        entityId,
        payload: {
          transaction_category_uuid: category,
          transaction_ids: selectedTxnIds.map(([key, _]) => key).join(", "),
        },
      }).unwrap();
      onClose();
      deselectRows();
      setRowSelection({});
      successToast({
        title: `Recategorisation successful`,
        message: `For ${selectedTxnIds.length} transactions`,
      });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const tableData = selectedTransactions.map((transaction) => {
    const { merchant } = transaction;
    return {
      transaction: {
        ...transaction,
        merchant: merchant?.name || null,
        logo: merchant?.logo || null,
      },
    };
  }) as unknown as Transactions[];

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={onClose}>
        <Formik
          initialValues={{ category: "" }}
          onSubmit={onSubmit}
          validateOnChange
          validateOnMount
          validationSchema={object({
            category: string().required("Please select Category"),
          })}
        >
          {({
            submitForm,
            isSubmitting,
            isValid,
            values: { category },
            resetForm,
            setFieldValue,
          }) => {
            const flattenedCOAs = flattenTypes({
              accounts: chartOfAccounts,
            }).map((account) => {
              const label = getCOALabel({ account });

              return {
                value: account.uuid,
                label: label,
                data: label,
                isDisabled: account.topLevel,
              };
            });

            const selectedCategory =
              flattenedCOAs.find(({ value }) => value === category) || null;

            return (
              <Modal.Content
                useCustomOverlay
                size={Boolean(category) ? "xl" : "large"}
              >
                <Modal.Header>
                  <Modal.Title>Recategorise</Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body className="t-flex t-flex-col t-gap-6">
                  <Form className="t-m-0 t-flex t-flex-col t-gap-4">
                    <Combobox
                      withForm
                      name="category"
                      label="Category"
                      placeholder="Select account"
                      menuPortalTarget={document.body}
                      value={selectedCategory}
                      options={flattenChartOfAccounts}
                      filterOption={(v, i) =>
                        v.data.data
                          ?.toLocaleLowerCase()
                          .includes(i.toLocaleLowerCase()) || false
                      }
                      actions={
                        <Button
                          customType="link"
                          type="button"
                          onClick={parentSelectorModal.open}
                        >
                          Add a new account
                        </Button>
                      }
                    />

                    {category && (
                      <AnimatePresence>
                        <DisclaimerUI
                          txnCount={selectedTxnIds.length}
                          selectedCategory={selectedCategory?.label || ""}
                        />

                        <motion.div
                          layout
                          initial={{ opacity: 0, y: 100 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            ease: "easeInOut",
                            duration: 0.5,
                          }}
                        >
                          <TransactionsView
                            transactions={tableData}
                            rowSelection={rowSelection}
                            setRowSelection={setRowSelection}
                            showCheckbox
                          />
                        </motion.div>
                      </AnimatePresence>
                    )}
                  </Form>
                </Modal.Body>
                <Modal.Footer>
                  <div className="t-flex t-justify-end t-gap-3">
                    <Button
                      type="reset"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      customType="primary"
                      onClick={submitForm}
                      isLoading={isSubmitting}
                      disabled={
                        isSubmitting ||
                        !isValid ||
                        Boolean(selectedTxnIds.length === 0)
                      }
                    >
                      Recategorise
                    </Button>
                  </div>
                </Modal.Footer>
                {parentSelectorModal.isOpen && (
                  <AddNewAccount
                    isOpen={parentSelectorModal.isOpen}
                    close={parentSelectorModal.close}
                    open={parentSelectorModal.open}
                    onSuccess={({ uuid }) => {
                      setFieldValue("category", uuid);
                    }}
                  />
                )}
              </Modal.Content>
            );
          }}
        </Formik>
      </Modal.Root>
    </>
  );
};
